import {
  SequenceMenuItemType,
  Button,
  type ActiveFilter,
  IconPlus,
  Link
} from '@sequencehq/core-components'
import { Dispatch, FC, memo, SetStateAction, useState } from 'react'
import { InvoicesMagicTable } from 'components/Invoices/InvoicesMagicTable/InvoicesMagicTable'
import Page from 'components/Page'
import { Outlet, useSearchParams } from 'react-router-dom'
import { match } from 'ts-pattern'
import { InvoicesGrid } from 'components/Invoices/InvoicesGrid'
import { InvoicesExportToCsv } from 'components/Invoices/InvoicesExportToCsv'
import { SavedViewEditor } from 'SavedViews/components/SavedViewEditor'
import { SavedViewTitle } from 'SavedViews/components/SavedViewTitle'
import { invoicePaths } from '../../modules/Invoices'
import { ProtectedButton } from 'lib/rbac'
import { useSavedView } from 'SavedViews/hooks/useSavedView'

const Invoices: FC<{
  showSegmentedFilters?: boolean
  title?: string
  forcedActiveFilters?: ActiveFilter[]
  kebabMenuItems?: (
    setSavingFilters: Dispatch<SetStateAction<false | ActiveFilter[]>>
  ) => SequenceMenuItemType[] | undefined
  onUpdateSavedFilters?: (
    updatedActiveFilters: ActiveFilter[],
    sortBy: string | null
  ) => void
}> = memo(
  ({
    showSegmentedFilters = true,
    title = 'Invoices',
    forcedActiveFilters = [],
    kebabMenuItems,
    onUpdateSavedFilters
  }) => {
    const [searchParams] = useSearchParams()
    const view = searchParams.get('view')

    const {
      isSavedView,
      savingFilters,
      setSavingFilters,
      forceNewSavedView,
      onSaveFilters,
      onDismissSaveFilters,
      showSavedViewEditor
    } = useSavedView()

    const richTitle = showSavedViewEditor ? (
      <SavedViewEditor
        activeFilters={savingFilters || []}
        sortBy={searchParams.get('sortBy') ?? null}
        onDismiss={onDismissSaveFilters}
        forceNewSavedView={forceNewSavedView}
      />
    ) : (
      <SavedViewTitle
        title={title}
        kebabMenuItems={kebabMenuItems?.(setSavingFilters)}
      />
    )

    const addNewConfig = savingFilters
      ? undefined
      : {
          button: (
            <ProtectedButton permission="org.invoice:create">
              <Button
                as={Link.Unstyled}
                to={invoicePaths.new()}
                variant="primary"
                data-testid="invoices.new"
                leadingIcon={<IconPlus color="inherit" />}
              >
                New invoice
              </Button>
            </ProtectedButton>
          )
        }

    const [filtersApiQueryParams, setFiltersApiQueryParams] =
      useState<string>('')

    const menuButtons = savingFilters ? null : (
      <InvoicesExportToCsv filtersApiQueryParams={filtersApiQueryParams} />
    )

    return (
      <>
        <Page
          title={title}
          richTitle={richTitle}
          addNewConfig={addNewConfig}
          menuButtons={menuButtons}
          paddingBottom={0}
        >
          {match(view)
            .with('grid', () => <InvoicesGrid />)
            .otherwise(() => (
              <InvoicesMagicTable
                onSaveFilters={onSaveFilters}
                onUpdateSavedFilters={
                  isSavedView ? onUpdateSavedFilters : undefined
                }
                showSegmentedFilters={showSegmentedFilters}
                forcedActiveFilters={forcedActiveFilters}
                onFiltersApiQueryParamsChanged={newParams =>
                  setFiltersApiQueryParams(newParams)
                }
                lockedControls={savingFilters && savingFilters.length > 0}
              />
            ))}
        </Page>

        <Outlet />
      </>
    )
  }
)

export default Invoices
